import { useEffect, useState } from 'react'
import {
  Copy, KeyRound, Loader2, Pause, Play, Plus, RotateCw, Trash2, Webhook,
} from 'lucide-react'
import { ErpTable, Panel, StatusBadge, Td, Th } from '@/components/erp/primitives'
import { Drawer, DrawerField, DrawerSection } from '@/shared/ui/Drawer'
import { Modal } from '@/shared/ui/Modal'
import { CursorPager } from '@/shared/ui/CursorPager'
import { ErrorStrip, LoadingRows, Note } from '@/shared/ui/states'
import { useLoad } from '@/features/administration/hooks'
import { useCan } from '@/shared/auth/session'
import { explain } from '@/shared/api/problem'
import { fmtDateTime, humanise } from '@/shared/lib/format'
import { cn } from '@/lib/utils'
import type { DeliveryResponse, WebhookEndpointResponse } from '@/shared/api/types'
import {
  createWebhook, deleteWebhook, listDeliveries, listWebhookEventTypes, listWebhooks,
  pauseWebhook, resumeWebhook, retryDelivery, rotateWebhookSecret, updateWebhook, webhookReport,
} from './api'
import { deliveryTone, webhookReportModel } from './report'
import { RangePicker, ReportBody, rangeParams, type RangeId } from './ReportPanels'

/**
 * Outbound webhooks — the endpoints, and what has actually been delivered to them.
 *
 * The HMAC secret follows the same contract as an API key: supplied or generated
 * once, never readable again, rotatable. The rotation response is the only place
 * the new secret exists, so the UI that shows it has to say that plainly — there
 * is no support path that recovers it.
 */
export function WebhooksTab() {
  const canWebhook = useCan('integration:webhook')
  const hooks = useLoad(() => listWebhooks(), [])
  const [openId, setOpenId] = useState<string | null>(null)
  const [createOpen, setCreateOpen] = useState(false)
  const [issuedSecret, setIssuedSecret] = useState<{ name: string; secret: string } | null>(null)

  const rows = hooks.data ?? []

  return (
    <>
      <Panel bodyClassName="p-0">
        <ErrorStrip message={hooks.error} />
        <div className="px-4 py-2.5 border-b border-border flex items-center justify-between gap-3">
          <span className="text-erp-xs text-muted-foreground">
            Signed POSTs to a third party. A paused endpoint keeps its subscriptions but is skipped
            by the delivery runner.
          </span>
          {canWebhook && (
            <button type="button" className="erp-btn shrink-0" onClick={() => setCreateOpen(true)} aria-label="New endpoint">
              <Plus className="h-3.5 w-3.5" /> <span className="axis-hide-sm">New endpoint</span>
            </button>
          )}
        </div>

        <ErpTable head={<>
          <Th>Name</Th>
          <Th className="axis-hide-md">URL</Th>
          <Th>Events</Th>
          <Th>Status</Th>
          <Th className="axis-hide-lg">Last attempt</Th>
          <Th>Failures</Th>
        </>}>
          {hooks.loading && <LoadingRows colSpan={6} />}
          {!hooks.loading && rows.length === 0 && (
            <tr>
              <td colSpan={6} className="px-4 py-10 text-center text-erp-sm text-muted-foreground">
                No webhook endpoints registered.
              </td>
            </tr>
          )}
          {!hooks.loading && rows.map((h) => (
            <tr
              key={h.id}
              className="hover:bg-muted/40 cursor-pointer"
              onClick={() => setOpenId(h.id)}
              tabIndex={0}
              role="button"
              aria-label={`Open ${h.name}`}
              onKeyDown={(e) => {
                // Only when the row itself has focus. Otherwise Enter on a
                // nested button both activates it and opens the drawer, and
                // Space is swallowed by preventDefault so the button never fires.
                if (e.target !== e.currentTarget) return
                if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpenId(h.id) }
              }}
            >
              <Td className="font-medium">{h.name}</Td>
              <Td className="axis-hide-md font-mono text-erp-xs truncate max-w-[18rem]">{h.url}</Td>
              <Td className="text-erp-xs">{h.eventTypes?.length ?? 0}</Td>
              <Td><StatusBadge status={h.status.toLowerCase()} /></Td>
              <Td className="axis-hide-lg text-erp-xs">
                {h.lastTriggeredAt ? (
                  <span>
                    {fmtDateTime(h.lastTriggeredAt)}
                    {h.lastResponseStatus != null && (
                      <span className={cn(
                        'ml-1.5 tabular-nums',
                        h.lastResponseStatus >= 200 && h.lastResponseStatus < 300
                          ? 'text-emerald-700 dark:text-emerald-400'
                          : 'text-red-700 dark:text-red-400',
                      )}>
                        {h.lastResponseStatus}
                      </span>
                    )}
                  </span>
                ) : '—'}
              </Td>
              <Td className={cn('erp-metric', h.consecutiveFailures > 0 && 'text-red-700 dark:text-red-400')}>
                {h.consecutiveFailures}
              </Td>
            </tr>
          ))}
        </ErpTable>
      </Panel>

      <WebhookDrawer
        endpointId={openId}
        endpoints={rows}
        canWebhook={canWebhook}
        onClose={() => setOpenId(null)}
        onChanged={() => void hooks.refresh()}
        onSecret={(name, secret) => setIssuedSecret({ name, secret })}
      />

      {createOpen && (
        <WebhookFormModal
          onClose={() => setCreateOpen(false)}
          onSaved={() => { setCreateOpen(false); void hooks.refresh() }}
        />
      )}

      <SecretOnceModal issued={issuedSecret} onClose={() => setIssuedSecret(null)} />
    </>
  )
}

// ---------------------------------------------------------------------------

function WebhookDrawer({
  endpointId, endpoints, canWebhook, onClose, onChanged, onSecret,
}: {
  endpointId: string | null
  endpoints: WebhookEndpointResponse[]
  canWebhook: boolean
  onClose: () => void
  onChanged: () => void
  onSecret: (name: string, secret: string) => void
}) {
  const [tab, setTab] = useState<'detail' | 'deliveries' | 'report'>('detail')
  // Same reason as the API key drawer: a new endpoint opens on Detail rather
  // than inheriting the previous one's tab.
  useEffect(() => { setTab('detail') }, [endpointId])
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [editOpen, setEditOpen] = useState(false)

  const h = endpoints.find((e) => e.id === endpointId) ?? null

  const run = async (key: string, fn: () => Promise<unknown>) => {
    setBusy(key)
    setError(null)
    try {
      await fn()
      onChanged()
    } catch (e) {
      setError(explain(e))
    } finally {
      setBusy(null)
    }
  }

  return (
    <Drawer
      open={endpointId !== null}
      onClose={onClose}
      title={h?.name ?? 'Webhook endpoint'}
      subtitle={h?.url}
      width="lg"
      right={
        canWebhook && h ? (
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              className="erp-btn"
              disabled={busy !== null}
              onClick={() =>
                void run('toggle', () => (h.status === 'ACTIVE' ? pauseWebhook(h.id) : resumeWebhook(h.id)))
              }
            >
              {h.status === 'ACTIVE' ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
              {h.status === 'ACTIVE' ? 'Pause' : 'Resume'}
            </button>
            <button type="button" className="erp-btn" onClick={() => setEditOpen(true)}>Edit</button>
          </div>
        ) : undefined
      }
    >
      <ErrorStrip message={error} />

      <div className="flex items-center gap-0.5 border-b border-border px-4">
        {(['detail', 'deliveries', 'report'] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={cn(
              'px-3 py-2 text-erp-sm border-b-2 -mb-px capitalize',
              tab === t
                ? 'border-chocolate text-chocolate font-medium'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === 'detail' && h && (
        <>
          <DrawerSection title="Endpoint">
            <DrawerField label="URL" value={h.url} mono />
            <DrawerField label="Description" value={h.description ?? '—'} />
            <DrawerField label="Status" value={humanise(h.status)} />
            <DrawerField label="Max attempts" value={h.maxAttempts} />
            <DrawerField label="Consecutive failures" value={h.consecutiveFailures} />
            <DrawerField label="Last success" value={fmtDateTime(h.lastSuccessAt) || 'Never'} />
            <DrawerField label="Last failure" value={fmtDateTime(h.lastFailureAt) || 'Never'} />
            <DrawerField
              label="Last response"
              value={h.lastResponseStatus != null ? `HTTP ${h.lastResponseStatus}` : '—'}
            />
          </DrawerSection>

          <DrawerSection title="Subscribed events">
            {h.eventTypes?.length ? (
              <ul className="flex flex-wrap gap-1.5 px-4 py-2">
                {h.eventTypes.map((e) => (
                  <li key={e} className="px-1.5 py-0.5 rounded-md border border-border bg-muted/40 font-mono text-erp-xs">
                    {e}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="px-4 py-2 text-erp-sm text-muted-foreground">
                No events subscribed — this endpoint will never fire.
              </p>
            )}
          </DrawerSection>

          {canWebhook && (
            <DrawerSection title="Signing secret">
              <div className="px-4 py-3 space-y-2">
                <p className="text-erp-sm text-muted-foreground">
                  Deliveries are signed with an HMAC over the timestamp and body. The secret is
                  stored in the credential vault and never returned — rotating issues a new one,
                  shown once.
                </p>
                <button
                  type="button"
                  className="erp-btn"
                  disabled={busy !== null}
                  onClick={async () => {
                    setBusy('rotate')
                    setError(null)
                    try {
                      const r = await rotateWebhookSecret(h.id)
                      onSecret(h.name, r.secret)
                      onChanged()
                    } catch (e) {
                      setError(explain(e))
                    } finally {
                      setBusy(null)
                    }
                  }}
                >
                  {busy === 'rotate' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCw className="h-3.5 w-3.5" />}
                  Rotate secret
                </button>
                <p className="text-erp-xs text-muted-foreground">
                  The receiver must be updated at the same time — deliveries signed with the new
                  secret will fail verification against the old one.
                </p>
              </div>
            </DrawerSection>
          )}

          {canWebhook && (
            <DrawerSection title="Danger zone">
              <div className="px-4 py-3">
                <button
                  type="button"
                  className="erp-btn text-red-700 dark:text-red-400 border-red-300"
                  disabled={busy !== null}
                  onClick={() => {
                    if (!window.confirm(`Delete "${h.name}"? Its delivery history goes with it.`)) return
                    void run('delete', async () => { await deleteWebhook(h.id); onClose() })
                  }}
                >
                  <Trash2 className="h-3.5 w-3.5" /> Delete endpoint
                </button>
              </div>
            </DrawerSection>
          )}
        </>
      )}

      {tab === 'deliveries' && h && <DeliveriesPanel endpointId={h.id} canRetry={canWebhook} />}
      {tab === 'report' && h && <WebhookReportPanel endpointId={h.id} />}

      {editOpen && h && (
        <WebhookFormModal
          existing={h}
          onClose={() => setEditOpen(false)}
          onSaved={() => { setEditOpen(false); onChanged() }}
        />
      )}
    </Drawer>
  )
}

// ---------------------------------------------------------------------------

function DeliveriesPanel({ endpointId, canRetry }: { endpointId: string; canRetry: boolean }) {
  const [status, setStatus] = useState('')
  const [cursor, setCursor] = useState<string | undefined>()
  const [stack, setStack] = useState<string[]>([])
  const [error, setError] = useState<string | null>(null)

  const page = useLoad(
    () => listDeliveries(endpointId, { status: status || undefined, cursor, size: 25 }),
    [endpointId, status, cursor],
  )
  const rows = page.data?.content ?? []

  return (
    <div className="p-4 space-y-3">
      <ErrorStrip message={error ?? page.error} />
      <div className="flex items-center gap-2">
        <span className="text-erp-xs text-muted-foreground">Status</span>
        <select
          className="erp-input h-8"
          value={status}
          onChange={(e) => { setStatus(e.target.value); setCursor(undefined); setStack([]) }}
        >
          <option value="">All</option>
          {['PENDING', 'DELIVERED', 'FAILED', 'EXHAUSTED'].map((s) => (
            <option key={s} value={s}>{humanise(s)}</option>
          ))}
        </select>
      </div>

      <Panel bodyClassName="p-0">
        <ErpTable head={<>
          <Th>Event</Th>
          <Th>Status</Th>
          <Th>Attempt</Th>
          <Th className="axis-hide-md">Response</Th>
          <Th />
        </>}>
          {page.loading && <LoadingRows colSpan={5} />}
          {!page.loading && rows.length === 0 && (
            <tr>
              <td colSpan={5} className="px-4 py-8 text-center text-erp-sm text-muted-foreground">
                No deliveries recorded.
              </td>
            </tr>
          )}
          {rows.map((d: DeliveryResponse) => (
            <tr key={d.id} className="hover:bg-muted/40">
              <Td className="font-mono text-erp-xs">{d.eventType}</Td>
              <Td>
                <span className="inline-flex items-center gap-1.5 text-erp-xs">
                  <span className={cn(
                    'h-1.5 w-1.5 rounded-full',
                    deliveryTone(d.status) === 'good' && 'bg-emerald-600',
                    deliveryTone(d.status) === 'warn' && 'bg-amber-500',
                    deliveryTone(d.status) === 'bad' && 'bg-red-600',
                  )} />
                  {humanise(d.status)}
                </span>
              </Td>
              <Td className="tabular-nums text-erp-xs">{d.attempt}</Td>
              <Td className="axis-hide-md tabular-nums text-erp-xs">{d.responseStatus ?? '—'}</Td>
              <Td className="text-right">
                {canRetry && d.status !== 'DELIVERED' && (
                  <button
                    type="button"
                    className="erp-btn"
                    onClick={async () => {
                      setError(null)
                      try {
                        await retryDelivery(endpointId, d.id)
                        await page.refresh()
                      } catch (e) { setError(explain(e)) }
                    }}
                  >
                    Retry
                  </button>
                )}
              </Td>
            </tr>
          ))}
        </ErpTable>
      </Panel>

      <CursorPager
        page={stack.length}
        count={rows.length}
        hasMore={page.data?.hasMore ?? false}
        canGoBack={stack.length > 0}
        loading={page.loading}
        unit="deliveries"
        onNext={() => {
          const next = page.data?.nextCursor
          if (!next) return
          setStack((s) => [...s, cursor ?? ''])
          setCursor(next)
        }}
        onBack={() => {
          // Pop outside the updater — state updaters must be pure, and under
          // StrictMode's double-invocation a setState inside one runs twice.
          const copy = [...stack]
          const prev = copy.pop()
          setStack(copy)
          setCursor(prev || undefined)
        }}
      />
    </div>
  )
}

function WebhookReportPanel({ endpointId }: { endpointId: string }) {
  const [range, setRange] = useState<RangeId>('7d')
  const report = useLoad(() => webhookReport(endpointId, rangeParams(range)), [endpointId, range])
  const model = report.data ? webhookReportModel(report.data) : null

  return (
    <div className="p-4 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <span className="text-erp-sm text-muted-foreground inline-flex items-center gap-1.5">
          <Webhook className="h-3.5 w-3.5" /> Delivery attempts
        </span>
        <RangePicker value={range} onChange={setRange} onRefresh={() => void report.refresh()} busy={report.loading} />
      </div>
      <ReportBody
        model={model}
        error={report.error}
        loading={report.loading}
        emptyHint="No deliveries were attempted in this window. Note that nothing in the backend currently publishes webhook events, so an empty report here is expected until a publisher is wired."
      />
    </div>
  )
}

// ---------------------------------------------------------------------------

/**
 * Create or edit an endpoint.
 *
 * The event picker is driven by `GET /webhooks/event-types`. That endpoint
 * currently returns an empty array — the backend's catalogue scanner was built
 * without an include filter — so the form falls back to free entry **and says
 * why**. An empty picker with no explanation reads as "this system has no
 * events", which is the wrong conclusion and the reason a subscription would
 * silently never fire.
 */
function WebhookFormModal({
  existing, onClose, onSaved,
}: {
  existing?: WebhookEndpointResponse
  onClose: () => void
  onSaved: () => void
}) {
  const catalogue = useLoad(() => listWebhookEventTypes(), [])
  const [name, setName] = useState(existing?.name ?? '')
  const [url, setUrl] = useState(existing?.url ?? '')
  const [description, setDescription] = useState(existing?.description ?? '')
  const [secret, setSecret] = useState('')
  const [maxAttempts, setMaxAttempts] = useState(String(existing?.maxAttempts ?? 6))
  const [events, setEvents] = useState<string[]>(existing?.eventTypes ?? [])
  const [freeEvent, setFreeEvent] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const known = catalogue.data ?? []
  const editing = Boolean(existing)

  const toggle = (code: string) =>
    setEvents((e) => (e.includes(code) ? e.filter((x) => x !== code) : [...e, code]))

  const submit = async () => {
    if (!name.trim() || !url.trim()) { setError('Name and URL are both required.'); return }
    if (!editing && !secret.trim()) { setError('A signing secret is required.'); return }
    setSaving(true)
    setError(null)
    try {
      if (existing) {
        await updateWebhook(existing.id, {
          name: name.trim(),
          url: url.trim(),
          description: description.trim() || null,
          eventTypes: events,
          maxAttempts: Number(maxAttempts) || 6,
        })
      } else {
        await createWebhook({
          name: name.trim(),
          url: url.trim(),
          description: description.trim() || null,
          eventTypes: events,
          secret: secret.trim(),
          maxAttempts: Number(maxAttempts) || 6,
        })
      }
      onSaved()
    } catch (e) {
      setError(explain(e))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={editing ? 'Edit endpoint' : 'New webhook endpoint'}
      description={
        editing
          ? 'The signing secret is rotated separately — it is never edited in place.'
          : 'Deliveries are signed with the secret you supply. Store it on the receiving side now; it is not retrievable afterwards.'
      }
      footer={
        <>
          <button type="button" className="erp-btn" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="button" className="erp-btn erp-btn-primary" onClick={() => void submit()} disabled={saving}>
            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />} {editing ? 'Save' : 'Create'}
          </button>
        </>
      }
    >
      <ErrorStrip message={error} />

      <label className="block">
        <span className="text-erp-xs text-muted-foreground">Name</span>
        <input className="erp-input w-full mt-1" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
      </label>

      <label className="block mt-3">
        <span className="text-erp-xs text-muted-foreground">URL</span>
        <input
          className="erp-input w-full mt-1 font-mono"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://example.org/hooks/axis"
        />
      </label>

      <label className="block mt-3">
        <span className="text-erp-xs text-muted-foreground">Description</span>
        <input className="erp-input w-full mt-1" value={description} onChange={(e) => setDescription(e.target.value)} />
      </label>

      {!editing && (
        <label className="block mt-3">
          <span className="text-erp-xs text-muted-foreground">Signing secret</span>
          <input
            type="password"
            autoComplete="off"
            className="erp-input w-full mt-1 font-mono"
            value={secret}
            onChange={(e) => setSecret(e.target.value)}
          />
        </label>
      )}

      <div className="mt-3">
        <span className="text-erp-xs text-muted-foreground">Subscribed events</span>
        {known.length > 0 ? (
          <ul className="mt-1 flex flex-wrap gap-1.5">
            {known.map((k) => (
              <li key={k.code}>
                <button
                  type="button"
                  onClick={() => toggle(k.code)}
                  title={k.description ?? undefined}
                  className={cn(
                    'px-2 py-1 rounded-md border text-erp-xs font-mono',
                    events.includes(k.code)
                      ? 'border-chocolate/50 bg-chocolate-soft/50 text-chocolate'
                      : 'border-input text-muted-foreground hover:text-foreground',
                  )}
                >
                  {k.code}
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <div className="mt-1 space-y-2">
            <Note tone="info">
              The event catalogue came back empty, so there is nothing to pick from. That is a
              backend fault rather than an absence of events — type the codes by hand for now, and
              check them against the publisher, because a name that never fires looks identical to
              one that does.
            </Note>
            <div className="flex gap-1.5">
              <input
                className="erp-input flex-1 font-mono"
                value={freeEvent}
                onChange={(e) => setFreeEvent(e.target.value)}
                placeholder="voter_created"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && freeEvent.trim()) {
                    e.preventDefault()
                    toggle(freeEvent.trim())
                    setFreeEvent('')
                  }
                }}
              />
              <button
                type="button"
                className="erp-btn"
                onClick={() => { if (freeEvent.trim()) { toggle(freeEvent.trim()); setFreeEvent('') } }}
              >
                Add
              </button>
            </div>
          </div>
        )}

        {events.length > 0 && (
          <ul className="mt-2 flex flex-wrap gap-1.5">
            {events.map((e) => (
              <li key={e} className="px-1.5 py-0.5 rounded-md border border-chocolate/40 bg-chocolate-soft/40 text-chocolate font-mono text-erp-xs inline-flex items-center gap-1">
                {e}
                <button type="button" onClick={() => toggle(e)} aria-label={`Remove ${e}`}>×</button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <label className="block mt-3">
        <span className="text-erp-xs text-muted-foreground">Max attempts</span>
        <input
          type="number"
          min={1}
          max={20}
          className="erp-input w-full mt-1"
          value={maxAttempts}
          onChange={(e) => setMaxAttempts(e.target.value)}
        />
      </label>
    </Modal>
  )
}

/** The rotated secret exists here and nowhere else. Say so, loudly. */
function SecretOnceModal({
  issued, onClose,
}: {
  issued: { name: string; secret: string } | null
  onClose: () => void
}) {
  const [copied, setCopied] = useState(false)
  if (!issued) return null
  return (
    <Modal
      open
      onClose={onClose}
      title="New signing secret"
      description={`For "${issued.name}". This is the only time it will ever be shown.`}
      footer={<button type="button" className="erp-btn erp-btn-primary" onClick={onClose}>Done</button>}
    >
      <Note tone="bad">
        <span className="inline-flex items-start gap-1.5">
          <KeyRound className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <span>
            Copy this into the receiving system now. It is not stored anywhere retrievable and there
            is no support path that recovers it — deliveries signed with it will simply start
            failing verification if it is lost.
          </span>
        </span>
      </Note>
      <div className="mt-3 flex items-center gap-2">
        <code className="flex-1 font-mono text-erp-xs break-all bg-muted rounded-md px-2 py-2">
          {issued.secret}
        </code>
        <button
          type="button"
          className="erp-btn shrink-0"
          onClick={async () => {
            try { await navigator.clipboard.writeText(issued.secret) } catch { /* clipboard blocked */ }
            setCopied(true)
            setTimeout(() => setCopied(false), 1800)
          }}
        >
          <Copy className="h-3.5 w-3.5" /> {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
    </Modal>
  )
}
